import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { guardLeavingPage } from "../../../../../common/navigation/leaveGuard";

type ShowBlocker = () => void;
type Blocker = { step: string | undefined; show: ShowBlocker };

interface StepLeaveBlockers {
  block: (id: string, blocker: Blocker) => void;
  unblock: (id: string) => void;
  isHeldBack: boolean;
}

const StepLeaveBlockersContext = createContext<StepLeaveBlockers | null>(null);
const StepScopeContext = createContext<string | undefined>(undefined);

export const StepLeaveBlockersProvider = StepLeaveBlockersContext.Provider;
export const StepScope = StepScopeContext.Provider;

export const useStepLeaveBlockers = (
  currentStep?: string,
  returnTo?: (step: string) => void,
) => {
  const [blockers, setBlockers] = useState<Map<string, Blocker>>(new Map());
  const [triedToLeave, setTriedToLeave] = useState(false);
  const [showOnReturn, setShowOnReturn] = useState<string | null>(null);
  const holding = useMemo(
    () =>
      [...blockers.values()].filter(
        (blocker) => blocker.step === undefined || blocker.step === currentStep,
      ),
    [blockers, currentStep],
  );
  const isBlocked = holding.length > 0;

  const holdBack = useCallback((): boolean => {
    const first = holding[0];
    if (!first) return false;
    setTriedToLeave(true);
    first.show();
    return true;
  }, [holding]);

  const leaveOr = useCallback(
    (leave?: () => void) => () => {
      if (holdBack()) return;
      setTriedToLeave(false);
      leave?.();
    },
    [holdBack],
  );

  const previousStep = useRef(currentStep);
  useEffect(() => {
    const left = previousStep.current;
    previousStep.current = currentStep;
    if (left === undefined || left === currentStep || !returnTo) return;
    if (![...blockers.values()].some((blocker) => blocker.step === left))
      return;
    setTriedToLeave(true);
    setShowOnReturn(left);
    returnTo(left);
  }, [currentStep, blockers, returnTo]);

  useEffect(() => {
    if (showOnReturn === null || showOnReturn !== currentStep) return;
    setShowOnReturn(null);
    holding[0]?.show();
  }, [showOnReturn, currentStep, holding]);

  useEffect(() => {
    if (!isBlocked) return;
    const askBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", askBeforeUnload);
    const stopGuarding = guardLeavingPage(() => !holdBack());
    return () => {
      window.removeEventListener("beforeunload", askBeforeUnload);
      stopGuarding();
    };
  }, [isBlocked, holdBack]);

  const register = useMemo(
    () => ({
      block: (id: string, blocker: Blocker) =>
        setBlockers((current) => new Map(current).set(id, blocker)),
      unblock: (id: string) =>
        setBlockers((current) => {
          if (!current.has(id)) return current;
          const next = new Map(current);
          next.delete(id);
          return next;
        }),
    }),
    [],
  );
  const value = useMemo<StepLeaveBlockers>(
    () => ({ ...register, isHeldBack: triedToLeave && isBlocked }),
    [register, triedToLeave, isBlocked],
  );

  return { blockers: value, leaveOr };
};

export const useStepLeaveHeldBack = (): boolean =>
  useContext(StepLeaveBlockersContext)?.isHeldBack ?? false;

export const useBlockStepLeave = (
  id: string,
  isBlocked: boolean,
  show: ShowBlocker,
) => {
  const blockers = useContext(StepLeaveBlockersContext);
  const step = useContext(StepScopeContext);
  const block = blockers?.block;
  const unblock = blockers?.unblock;
  const latestShow = useRef(show);
  latestShow.current = show;
  useEffect(() => {
    if (!block || !unblock) return;
    if (isBlocked) block(id, { step, show: () => latestShow.current() });
    else unblock(id);
    return () => unblock(id);
  }, [block, unblock, id, step, isBlocked]);
};
